import { getAuditTrailData, extractVersionStructure } from '../../../../util/getAuditTrailData';
import { calculateGlobalNodeNumberByType, compareSiblingOrders } from '../components/Nodes/utils';
import { generateTitlesFromStructure, uploadJsonToS3, prepareCurrentSnapshot, getRevAndTraining } from '../../../../util/AppUtils';

const CHILD_NODE_DETAIL_FIELDS = [
  'canvas_tree_node_id',
  'attachment_tree_node_id',
  'video_tree_node_id',
  'rich_text_node_id',
  'form_tree_node_id',
];

/**
 * Calculate move/copy parameters for child nodes (attachment, video, rich-text, canvas, form) based on position
 * @param {Object} params - Parameters object
 * @param {string} params.position - 'into' | 'above' | 'below'
 * @param {Object} params.targetAllNodes - All nodes from target document
 * @param {Object} params.targetDoc - Target document info
 * @param {Object} params.movePosition - Position details with targetId and targetType
 * @param {boolean} params.moveCreateCopy - Whether this is a copy operation
 * @param {Object} params.sourceChildNode - Source child node being moved
 * @param {Object} params.moveDocument - Source document info (to check if same document)
 * @param {string} params.childNodeType - Type of child node ('attachment', 'video', 'rich-text', 'canvas', 'form')
 * @returns {Object} - { toOrder, toProcedureTreeNodeRef, toSectionIdRef, toStepIdRef, toSubstepIdRef, toChildNodeRef, direction, direction_move_into }
 */
export const calculateChildNodeMoveParameters = ({
  position,
  targetAllNodes,
  targetDoc,
  movePosition,
  moveCreateCopy,
  sourceChildNode,
  moveDocument,
  childNodeType,
}) => {
  let toOrder, toProcedureTreeNodeRef, toSectionIdRef, toStepIdRef, toSubstepIdRef, toChildNodeRef, direction, direction_move_into;
  
  let targetSection = null;
  let targetStep = null;
  let targetSubstep = null;
  let targetChildNode = null;

  // Get the node ID key based on child node type
  const getNodeIdKey = (type) => {
    const keyMap = {
      'attachment': 'attachment_tree_node_id',
      'video': 'video_tree_node_id',
      'rich-text': 'rich_text_node_id',
      'canvas': 'canvas_tree_node_id',
      'canva': 'canvas_tree_node_id',
      'form': 'form_tree_node_id',
    };
    return keyMap[type] || 'tree_node_id';
  };

  const nodeIdKey = getNodeIdKey(childNodeType);

  if (position === 'into') {
    // For "into", target can be a step or substep
    if (movePosition.targetType === 'step') {
      // Target is a step
      for (const section of targetAllNodes) {
        targetStep = section.steps?.find(
          step => step.tree_node_id === movePosition.targetId || 
                  step.step_id_ref === movePosition.targetId
        );
        if (targetStep) {
          targetSection = section;
          break;
        }
      }

      if (!targetStep || !targetSection) {
        throw new Error('Target step not found.');
      }

      // Get all child nodes in target step (excluding substep children)
      const targetStepChildren = (targetStep.children || []).filter(
        child => !child.substep_id_ref && !child.sub_step_tree_node_id
      );
      
      // Calculate max order and add 1
      const allOrders = targetStepChildren
        .map((child) =>
          Number(child.sibling_order || child.procedure_tree?.sibling_order || 0)
        )
        .filter((order) => !isNaN(order));

      toOrder = allOrders.length > 0 ? Math.max(...allOrders) + 1 : 1;
      
      // For "into" step: to_procedure_tree_node_ref = last child node's tree_node_id (or step's tree_node_id if no children)
      if (targetStepChildren.length > 0) {
        // Sort children by order and get the last one
        const sortedChildren = [...targetStepChildren].sort((a, b) => {
          const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
          const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
          return orderA - orderB;
        });
        toProcedureTreeNodeRef = sortedChildren[sortedChildren.length - 1].tree_node_id;
      } else {
        // No children in target step, use step's tree_node_id
        toProcedureTreeNodeRef = targetStep.tree_node_id;
      }
      
      toSectionIdRef = targetSection.section_id_ref;
      toStepIdRef = targetStep.step_id_ref;
      toSubstepIdRef = null;
      toChildNodeRef = null; // For "into", this field is omitted
      direction = moveCreateCopy ? 'paste_down' : 'move_down';
      direction_move_into = true; // Always true for "into"
      
    } else if (movePosition.targetType === 'substep') {
      // Target is a substep
      for (const section of targetAllNodes) {
        for (const step of section.steps || []) {
          targetSubstep = step.substeps?.find(
            substep => substep.tree_node_id === movePosition.targetId || 
                       substep.substep_id_ref === movePosition.targetId
          );
          if (targetSubstep) {
            targetStep = step;
            targetSection = section;
            break;
          }
        }
        if (targetSubstep) break;
      }

      if (!targetSubstep || !targetStep || !targetSection) {
        throw new Error('Target substep not found.');
      }

      // Get all child nodes in target substep
      const targetSubstepChildren = targetSubstep.children || [];
      
      // Calculate max order and add 1
      const allOrders = targetSubstepChildren
        .map((child) =>
          Number(child.sibling_order || child.procedure_tree?.sibling_order || 0)
        )
        .filter((order) => !isNaN(order));

      toOrder = allOrders.length > 0 ? Math.max(...allOrders) + 1 : 1;
      
      // For "into" substep: to_procedure_tree_node_ref = last child node's tree_node_id (or substep's tree_node_id if no children)
      if (targetSubstepChildren.length > 0) {
        // Sort children by order and get the last one
        const sortedChildren = [...targetSubstepChildren].sort((a, b) => {
          const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
          const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
          return orderA - orderB;
        });
        toProcedureTreeNodeRef = sortedChildren[sortedChildren.length - 1].tree_node_id;
      } else {
        // No children in target substep, use substep's tree_node_id
        toProcedureTreeNodeRef = targetSubstep.tree_node_id;
      }
      
      toSectionIdRef = targetSection.section_id_ref;
      toStepIdRef = targetStep.step_id_ref;
      toSubstepIdRef = targetSubstep.substep_id_ref;
      toChildNodeRef = null; // For "into", this field is omitted
      direction = moveCreateCopy ? 'paste_down' : 'move_down';
      direction_move_into = true; // Always true for "into"
    }
  } else {
    // For "above" or "below", target is another child node
    // Helper function to check if a child node matches the targetId
    // Must check all possible ID fields since target could be any child node type
    const matchesTargetId = (child) => {
      // Check tree_node_id first (most common)
      if (child.tree_node_id === movePosition.targetId) return true;
      
      // Check all possible detail ID fields for different child node types
      const detailIdFields = [
        'canvas_tree_node_id',
        'attachment_tree_node_id',
        'video_tree_node_id',
        'rich_text_node_id',
        'form_tree_node_id',
      ];
      
      for (const field of detailIdFields) {
        if (child.detail?.[field] === movePosition.targetId) return true;
      }
      
      // Also check if nodeIdKey matches (for the source node type)
      if (child.detail?.[nodeIdKey] === movePosition.targetId) return true;
      
      return false;
    };
    
    // First, find the target child node in all sections, steps, and substeps
    for (const section of targetAllNodes) {
      for (const step of section.steps || []) {
        // Check step-level children
        targetChildNode = step.children?.find(matchesTargetId);
        if (targetChildNode) {
          targetStep = step;
          targetSection = section;
          targetSubstep = null;
          break;
        }

        // Check substep-level children
        for (const substep of step.substeps || []) {
          targetChildNode = substep.children?.find(matchesTargetId);
          if (targetChildNode) {
            targetStep = step;
            targetSection = section;
            targetSubstep = substep;
            break;
          }
        }
        if (targetChildNode) break;
      }
      if (targetChildNode) break;
    }

    if (!targetChildNode) {
      throw new Error('Target child node not found.');
    }

    const targetOrder = parseInt(targetChildNode.sibling_order || targetChildNode.procedure_tree?.sibling_order || '0', 10);
    const sourceOrder = sourceChildNode ? parseInt(sourceChildNode.sibling_order || sourceChildNode.procedure_tree?.sibling_order || '0', 10) : null;
    
    // Determine source and target containers
    const sourceStepIdRef = sourceChildNode?.step_id_ref;
    const sourceSubstepIdRef = sourceChildNode?.substep_id_ref || sourceChildNode?.sub_step_tree_node_id ? (sourceChildNode.substep_id_ref || sourceChildNode.sub_step_tree_node_id) : null;
    const targetStepIdRef = targetSubstep ? targetStep.step_id_ref : targetStep.step_id_ref;
    const targetSubstepIdRef = targetSubstep ? targetSubstep.substep_id_ref : null;
    
    // Check if this is a same-document, same-container move (not copy)
    const isSameDocumentMove = !moveCreateCopy && 
                               sourceChildNode && 
                               moveDocument && 
                               moveDocument.partId === targetDoc.partId && 
                               moveDocument.revisionId === targetDoc.revisionId;
    
    const isSameContainer = sourceStepIdRef === targetStepIdRef && 
                            sourceSubstepIdRef === targetSubstepIdRef;

    // For move operations in the same container, we need to account for the source child being removed
    if (isSameDocumentMove && isSameContainer && sourceOrder !== null) {
      // Same-container move: adjust order accounting for source removal
      if (sourceOrder < targetOrder) {
        // Source comes before target, target shifts down
        if (position === 'above') {
          toOrder = targetOrder - 1;
        } else {
          toOrder = targetOrder;
        }
      } else {
        // Source comes after target, target order doesn't change
        if (position === 'above') {
          toOrder = targetOrder;
        } else {
          toOrder = targetOrder + 1;
        }
      }
    } else {
      // For copy operations or cross-container moves
      if (position === 'above') {
        toOrder = targetOrder;
      } else {
        toOrder = targetOrder + 1;
      }
    }

    direction = moveCreateCopy ? (position === 'above' ? 'paste_up' : 'paste_down') : (position === 'above' ? 'move_up' : 'move_down');

    // Get target child node ID
    const targetChildNodeId = targetChildNode.detail?.[nodeIdKey] || targetChildNode.tree_node_id;
    
    // For ABOVE/BELOW: to_procedure_tree_node_ref = target child node's tree_node_id
    toProcedureTreeNodeRef = targetChildNode.tree_node_id;
    toSectionIdRef = targetSection.section_id_ref;
    toStepIdRef = targetStep.step_id_ref;
    toSubstepIdRef = targetSubstep ? targetSubstep.substep_id_ref : null;
    toChildNodeRef = targetChildNodeId; // Target child node's ID
    
    // direction_move_into logic:
    // - Same container: false (for moves), true (for copies)
    // - Cross container: true (for both moves and copies)
    if (isSameContainer) {
      direction_move_into = moveCreateCopy ? true : false; // Copies always true, moves false for same-container
    } else {
      // Cross-container: always true (for both moves and copies)
      direction_move_into = true;
    }
  }

  return {
    toOrder,
    toProcedureTreeNodeRef,
    toSectionIdRef,
    toStepIdRef,
    toSubstepIdRef,
    toChildNodeRef,
    direction,
    direction_move_into,
  };
};

const normalizeChildNodeType = (type) => {
  if (!type) return '';
  if (type === 'canvas' || type === 'canva') return 'canva';
  return type;
};

export const getChildNodeDisplayNumber = (childNode, childNodeType, allNodes) => {
  const fallbackValue = parseInt(
    childNode?.sibling_order ||
      childNode?.procedure_tree?.sibling_order ||
      '1',
    10,
  ) || 1;

  if (!childNode || !Array.isArray(allNodes) || allNodes.length === 0) {
    return fallbackValue;
  }

  try {
    const result = calculateGlobalNodeNumberByType(
      childNode,
      normalizeChildNodeType(childNodeType || childNode?.type),
      allNodes,
      compareSiblingOrders,
    );

    if (result && !Number.isNaN(result)) {
      return result;
    }
  } catch (error) {
    // Fall back to sibling order if global number calculation fails
  }

  return fallbackValue;
};

export const formatChildTypeLabel = (childNodeType) => {
  if (!childNodeType) return '';
  if (childNodeType === 'rich-text') return 'Rich Text';
  if (childNodeType === 'canva' || childNodeType === 'canvas') return 'Canvas';
  return childNodeType.charAt(0).toUpperCase() + childNodeType.slice(1);
};

const matchesId = (value, candidate) => {
  if (value === undefined || value === null || candidate === undefined || candidate === null) {
    return false;
  }
  return String(value) === String(candidate);
};

const getOrderValue = (node) => node?.sibling_order || node?.procedure_tree?.sibling_order || '0';

const sortBySiblingOrder = (items = []) => [...items].sort((a, b) => compareSiblingOrders(getOrderValue(a), getOrderValue(b)));

const calculateGlobalStepNumber = (allNodes, stepOrSubstep) => {
  if (!Array.isArray(allNodes) || !stepOrSubstep || !stepOrSubstep.step_id_ref) return 1;

  const sortedSections = sortBySiblingOrder(allNodes);
  let stepsBefore = 0;

  for (const section of sortedSections) {
    const steps = sortBySiblingOrder(section.steps || []);
    const stepIndex = steps.findIndex((step) =>
      matchesId(step.step_id_ref, stepOrSubstep.step_id_ref) || matchesId(step.tree_node_id, stepOrSubstep.step_id_ref)
    );

    if (stepIndex !== -1) {
      return stepsBefore + stepIndex + 1;
    }

    stepsBefore += steps.length;
  }

  return 1;
};

const getSubstepLetter = (allNodes, substep) => {
  if (!Array.isArray(allNodes) || !substep || !substep.step_id_ref) return '';

  const sortedSections = sortBySiblingOrder(allNodes);

  for (const section of sortedSections) {
    const steps = section.steps || [];
    for (const step of steps) {
      if (!matchesId(step.step_id_ref, substep.step_id_ref) && !matchesId(step.tree_node_id, substep.step_id_ref)) {
        continue;
      }

      const substeps = sortBySiblingOrder(step.substeps || []);
      const substepIndex = substeps.findIndex((current) =>
        matchesId(current.substep_id_ref, substep.substep_id_ref) || matchesId(current.tree_node_id, substep.tree_node_id)
      );

      if (substepIndex !== -1) {
        return String.fromCharCode(65 + substepIndex);
      }
      return '';
    }
  }

  return '';
};

const getSectionNumber = (allNodes, section) => {
  if (!Array.isArray(allNodes) || !section) return null;

  const sortedSections = sortBySiblingOrder(allNodes);
  const sectionIndex = sortedSections.findIndex((current) =>
    matchesId(current.section_id_ref, section.section_id_ref) || matchesId(current.tree_node_id, section.tree_node_id)
  );

  return sectionIndex !== -1 ? sectionIndex + 1 : null;
};

const getTargetSubstepDetails = (allNodes, movePosition) => {
  if (!Array.isArray(allNodes) || !movePosition) return null;

  const candidateIds = new Set();

  if (movePosition.targetType === 'substep' && movePosition.targetId) {
    candidateIds.add(movePosition.targetId);
  }

  ['targetSubstepId', 'targetSubstepTreeNodeId', 'targetSubstepIdRef', 'substepId', 'substep_id_ref', 'sub_step_tree_node_id'].forEach((key) => {
    if (movePosition[key]) {
      candidateIds.add(movePosition[key]);
    }
  });

  if (candidateIds.size === 0) {
    return null;
  }

  const sortedSections = sortBySiblingOrder(allNodes);

  for (const section of sortedSections) {
    const steps = section.steps || [];
    for (const step of steps) {
      for (const substep of step.substeps || []) {
        const matches = [...candidateIds].some((candidate) =>
          matchesId(substep.substep_id_ref, candidate) || matchesId(substep.tree_node_id, candidate)
        );

        if (matches) {
          const stepNumber = calculateGlobalStepNumber(allNodes, substep);
          const substepLetter = getSubstepLetter(allNodes, substep);
          const sectionNumber = getSectionNumber(allNodes, section);

          return {
            substep,
            step,
            section,
            identifier: `${stepNumber}${substepLetter || ''}`,
            stepNumber,
            substepLetter,
            sectionNumber,
          };
        }
      }
    }
  }

  return null;
};

const getTargetChildDetails = (allNodes, movePosition) => {
  if (!Array.isArray(allNodes) || !movePosition) return null;

  const candidateIds = new Set();
  [
    'targetId',
    'targetChildId',
    'targetChildTreeNodeId',
    'targetChildNodeRef',
    'targetChildNodeId',
    'to_child_node_ref',
    'toChildNodeRef',
    'childTreeNodeId',
  ].forEach((key) => {
    if (movePosition[key]) {
      candidateIds.add(movePosition[key]);
    }
  });

  if (candidateIds.size === 0) {
    return null;
  }

  const detailFields = [
    'canvas_tree_node_id',
    'attachment_tree_node_id',
    'video_tree_node_id',
    'rich_text_node_id',
    'form_tree_node_id',
  ];

  const sortedSections = sortBySiblingOrder(allNodes);

  for (const section of sortedSections) {
    const steps = section.steps || [];
    for (const step of steps) {
      // Step-level children
      for (const child of step.children || []) {
        const matches = [...candidateIds].some((candidate) => {
          if (matchesId(child.tree_node_id, candidate)) return true;
          return detailFields.some((field) => matchesId(child.detail?.[field], candidate));
        });

        if (matches) {
          const sectionNumber = getSectionNumber(allNodes, section);
          const stepNumber = calculateGlobalStepNumber(allNodes, step);
          return {
            child,
            section,
            step,
            substep: null,
            sectionNumber,
            stepNumber,
            substepIdentifier: '',
          };
        }
      }

      // Substep-level children
      for (const substep of step.substeps || []) {
        for (const child of substep.children || []) {
          const matches = [...candidateIds].some((candidate) => {
            if (matchesId(child.tree_node_id, candidate)) return true;
            if (matchesId(child.sub_step_tree_node_id, candidate)) return true;
            return detailFields.some((field) => matchesId(child.detail?.[field], candidate));
          });

          if (matches) {
            const sectionNumber = getSectionNumber(allNodes, section);
            const stepNumber = calculateGlobalStepNumber(allNodes, substep);
            const substepLetter = getSubstepLetter(allNodes, substep);
            return {
              child,
              section,
              step,
              substep,
              sectionNumber,
              stepNumber,
              substepIdentifier: `${stepNumber}${substepLetter || ''}`,
            };
          }
        }
      }
    }
  }

  return null;
};

/**
 * Prepare new content structure for child node move/copy preview
 * @param {Object} params - Parameters object
 * @param {Array} params.targetAllNodes - All nodes from target document
 * @param {Object} params.sourceChildNode - Source child node being moved/copied
 * @param {Object} params.moveDocument - Source document info
 * @param {Object} params.targetDoc - Target document info
 * @param {string} params.position - 'into' | 'above' | 'below'
 * @param {Object} params.movePosition - Position details
 * @param {string} params.childNodeType - Type of child node
 * @param {boolean} params.moveCreateCopy - Whether this is a copy operation
 * @returns {Array} - New content structure with child node moved/copied
 */
export const prepareChildNodeMoveNewContent = ({
  targetAllNodes,
  sourceChildNode,
  moveDocument,
  targetDoc,
  position,
  movePosition,
  childNodeType,
  moveCreateCopy,
}) => {
  const newContent = structuredClone(targetAllNodes);
  
  // Get the node ID key based on child node type
  const getNodeIdKey = (type) => {
    const keyMap = {
      'attachment': 'attachment_tree_node_id',
      'video': 'video_tree_node_id',
      'rich-text': 'rich_text_node_id',
      'canvas': 'canvas_tree_node_id',
      'canva': 'canvas_tree_node_id',
      'form': 'form_tree_node_id',
    };
    return keyMap[type] || 'tree_node_id';
  };

  const nodeIdKey = getNodeIdKey(childNodeType);
  const sourceChildNodeId = sourceChildNode.detail?.[nodeIdKey] || sourceChildNode.tree_node_id;
  const sourceStepIdRef = sourceChildNode?.step_id_ref;
  const sourceSubstepIdRef = sourceChildNode?.substep_id_ref || sourceChildNode?.sub_step_tree_node_id ? (sourceChildNode.substep_id_ref || sourceChildNode.sub_step_tree_node_id) : null;

  // Deep clone the source child node
  const clonedChildNode = structuredClone(sourceChildNode);

  if (position === 'into') {
    // Find target step or substep and add child node to end
    if (movePosition.targetType === 'step') {
      let targetStep = null;
      let targetSection = null;

      for (const section of newContent) {
        targetStep = section.steps?.find(
          step => step.tree_node_id === movePosition.targetId || 
                  step.step_id_ref === movePosition.targetId
        );
        if (targetStep) {
          targetSection = section;
          break;
        }
      }

      if (targetStep) {
        if (moveCreateCopy) {
          // Copy: just add to target step
          if (!targetStep.children) {
            targetStep.children = [];
          }

          const maxOrder = targetStep.children.length > 0 
            ? Math.max(...targetStep.children.map(c => parseInt(c.sibling_order || c.procedure_tree?.sibling_order || '0', 10) || 0))
            : 0;
            
          clonedChildNode.sibling_order = (maxOrder + 1).toString();
          if (clonedChildNode.procedure_tree) {
            clonedChildNode.procedure_tree.sibling_order = (maxOrder + 1).toString();
          }

          targetStep.children.push(clonedChildNode);
        } else {
          // Move: remove from source, add to target
          const isSameDocument = moveDocument.partId === targetDoc.partId && 
                                 moveDocument.revisionId === targetDoc.revisionId;
          
          if (isSameDocument) {
            if (sourceSubstepIdRef) {
              // Remove from source substep (when moving from substep to step)
              let sourceSubstep = null;
              let sourceStep = null;
              
              for (const section of newContent) {
                for (const step of section.steps || []) {
                  sourceSubstep = step.substeps?.find(
                    substep => substep.substep_id_ref === sourceSubstepIdRef ||
                              substep.tree_node_id === sourceSubstepIdRef
                  );
                  if (sourceSubstep) {
                    sourceStep = step;
                    break;
                  }
                }
                if (sourceSubstep) break;
              }
              
              if (sourceSubstep && sourceSubstep.children) {
                sourceSubstep.children = sourceSubstep.children.filter(
                  child => {
                    // For canvas nodes, check both detail.canvas_tree_node_id and tree_node_id
                    const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
                    const childCanvasId = childNodeType === 'canvas' || childNodeType === 'canva' 
                      ? (child.detail?.canvas_tree_node_id || child.tree_node_id)
                      : null;
                    const sourceCanvasId = childNodeType === 'canvas' || childNodeType === 'canva'
                      ? (sourceChildNode.detail?.canvas_tree_node_id || sourceChildNode.tree_node_id)
                      : null;
                    
                    // Check if this child matches the source by any ID field
                    const matches = (childId === sourceChildNodeId || child.tree_node_id === sourceChildNodeId) ||
                                   (childCanvasId && sourceCanvasId && childCanvasId === sourceCanvasId);
                    return !matches;
                  }
                );
              }
            } else if (sourceStepIdRef) {
              // Remove from source step
              let sourceStep = null;
              let sourceSection = null;
              
              for (const section of newContent) {
                sourceStep = section.steps?.find(step => step.step_id_ref === sourceStepIdRef);
                if (sourceStep) {
                  sourceSection = section;
                  break;
                }
              }
              
              if (sourceStep && sourceStep.children) {
                sourceStep.children = sourceStep.children.filter(
                  child => {
                    // For canvas nodes, check both detail.canvas_tree_node_id and tree_node_id
                    const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
                    const childCanvasId = childNodeType === 'canvas' || childNodeType === 'canva' 
                      ? (child.detail?.canvas_tree_node_id || child.tree_node_id)
                      : null;
                    const sourceCanvasId = childNodeType === 'canvas' || childNodeType === 'canva'
                      ? (sourceChildNode.detail?.canvas_tree_node_id || sourceChildNode.tree_node_id)
                      : null;
                    
                    // Check if this child matches the source by any ID field
                    const matches = (childId === sourceChildNodeId || child.tree_node_id === sourceChildNodeId) ||
                                   (childCanvasId && sourceCanvasId && childCanvasId === sourceCanvasId);
                    return !matches;
                  }
                );
              }
            }
          }
          // Add to target step
          if (!targetStep.children) {
            targetStep.children = [];
          }

          const maxOrder = targetStep.children.length > 0 
            ? Math.max(...targetStep.children.map(c => parseInt(c.sibling_order || c.procedure_tree?.sibling_order || '0', 10) || 0))
            : 0;
            
          clonedChildNode.sibling_order = (maxOrder + 1).toString();
          if (clonedChildNode.procedure_tree) {
            clonedChildNode.procedure_tree.sibling_order = (maxOrder + 1).toString();
          }

          targetStep.children.push(clonedChildNode);
        }
      }
    } else if (movePosition.targetType === 'substep') {
      let targetSubstep = null;
      let targetStep = null;
      let targetSection = null;

      for (const section of newContent) {
        for (const step of section.steps || []) {
          targetSubstep = step.substeps?.find(
            substep => substep.tree_node_id === movePosition.targetId || 
                       substep.substep_id_ref === movePosition.targetId
          );
          if (targetSubstep) {
            targetStep = step;
            targetSection = section;
            break;
          }
        }
        if (targetSubstep) break;
      }

      if (targetSubstep) {
        if (moveCreateCopy) {
          // Copy: just add to target substep
          if (!targetSubstep.children) {
            targetSubstep.children = [];
          }

          const maxOrder = targetSubstep.children.length > 0 
            ? Math.max(...targetSubstep.children.map(c => parseInt(c.sibling_order || c.procedure_tree?.sibling_order || '0', 10) || 0))
            : 0;
            
          clonedChildNode.sibling_order = (maxOrder + 1).toString();
          if (clonedChildNode.procedure_tree) {
            clonedChildNode.procedure_tree.sibling_order = (maxOrder + 1).toString();
          }

          targetSubstep.children.push(clonedChildNode);
        } else {
          // Move: remove from source, add to target
          const isSameDocument = moveDocument.partId === targetDoc.partId && 
                                 moveDocument.revisionId === targetDoc.revisionId;
          
          if (isSameDocument && sourceSubstepIdRef) {
            // Remove from source substep
            let sourceSubstep = null;
            let sourceStep = null;
            
            for (const section of newContent) {
              for (const step of section.steps || []) {
                sourceSubstep = step.substeps?.find(
                  substep => substep.substep_id_ref === sourceSubstepIdRef ||
                            substep.tree_node_id === sourceSubstepIdRef
                );
                if (sourceSubstep) {
                  sourceStep = step;
                  break;
                }
              }
              if (sourceSubstep) break;
            }
            
            if (sourceSubstep && sourceSubstep.children) {
              sourceSubstep.children = sourceSubstep.children.filter(
                child => {
                  // For canvas nodes, check both detail.canvas_tree_node_id and tree_node_id
                  const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
                  const childCanvasId = childNodeType === 'canvas' || childNodeType === 'canva' 
                    ? (child.detail?.canvas_tree_node_id || child.tree_node_id)
                    : null;
                  const sourceCanvasId = childNodeType === 'canvas' || childNodeType === 'canva'
                    ? (sourceChildNode.detail?.canvas_tree_node_id || sourceChildNode.tree_node_id)
                    : null;
                  
                  // Check if this child matches the source by any ID field
                  const matches = (childId === sourceChildNodeId || child.tree_node_id === sourceChildNodeId) ||
                                 (childCanvasId && sourceCanvasId && childCanvasId === sourceCanvasId);
                  return !matches;
                }
              );
            }
          } else if (isSameDocument && sourceStepIdRef && !sourceSubstepIdRef) {
            // Remove from source step (when moving from step to substep)
            let sourceStep = null;
            
            for (const section of newContent) {
              sourceStep = section.steps?.find(step => step.step_id_ref === sourceStepIdRef);
              if (sourceStep) break;
            }
            
            if (sourceStep && sourceStep.children) {
              sourceStep.children = sourceStep.children.filter(
                child => {
                  // For canvas nodes, check both detail.canvas_tree_node_id and tree_node_id
                  const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
                  const childCanvasId = childNodeType === 'canvas' || childNodeType === 'canva' 
                    ? (child.detail?.canvas_tree_node_id || child.tree_node_id)
                    : null;
                  const sourceCanvasId = childNodeType === 'canvas' || childNodeType === 'canva'
                    ? (sourceChildNode.detail?.canvas_tree_node_id || sourceChildNode.tree_node_id)
                    : null;
                  
                  // Check if this child matches the source by any ID field
                  const matches = (childId === sourceChildNodeId || child.tree_node_id === sourceChildNodeId) ||
                                 (childCanvasId && sourceCanvasId && childCanvasId === sourceCanvasId);
                  return !matches;
                }
              );
            }
          }
          // Add to target substep
          if (!targetSubstep.children) {
            targetSubstep.children = [];
          }

          const maxOrder = targetSubstep.children.length > 0 
            ? Math.max(...targetSubstep.children.map(c => parseInt(c.sibling_order || c.procedure_tree?.sibling_order || '0', 10) || 0))
            : 0;
            
          clonedChildNode.sibling_order = (maxOrder + 1).toString();
          if (clonedChildNode.procedure_tree) {
            clonedChildNode.procedure_tree.sibling_order = (maxOrder + 1).toString();
          }

          targetSubstep.children.push(clonedChildNode);
        }
      }
    }
  } else {
    // For "above" or "below", find target child node and insert
    let targetChildNode = null;
    let targetStep = null;
    let targetSubstep = null;
    let targetSection = null;

    // Helper function to check if a child node matches the targetId
    // Must check all possible ID fields since target could be any child node type
    const matchesTargetId = (child) => {
      // Check tree_node_id first (most common)
      if (child.tree_node_id === movePosition.targetId) return true;
      
      // Check all possible detail ID fields for different child node types
      const detailIdFields = [
        'canvas_tree_node_id',
        'attachment_tree_node_id',
        'video_tree_node_id',
        'rich_text_node_id',
        'form_tree_node_id',
      ];
      
      for (const field of detailIdFields) {
        if (child.detail?.[field] === movePosition.targetId) return true;
      }
      
      // Also check if nodeIdKey matches (for the source node type)
      if (child.detail?.[nodeIdKey] === movePosition.targetId) return true;
      
      return false;
    };

    // Find target child node
    for (const section of newContent) {
      for (const step of section.steps || []) {
        // Check step-level children
        targetChildNode = step.children?.find(matchesTargetId);
        if (targetChildNode) {
          targetStep = step;
          targetSection = section;
          targetSubstep = null;
          break;
        }

        // Check substep-level children
        for (const substep of step.substeps || []) {
          targetChildNode = substep.children?.find(matchesTargetId);
          if (targetChildNode) {
            targetStep = step;
            targetSection = section;
            targetSubstep = substep;
            break;
          }
        }
        if (targetChildNode) break;
      }
      if (targetChildNode) break;
    }

    if (targetChildNode) {
      const targetContainer = targetSubstep || targetStep;
      if (!targetContainer.children) {
        targetContainer.children = [];
      }

      const targetIndex = targetContainer.children.findIndex(matchesTargetId);

      if (targetIndex !== -1) {
        const isSameDocument = moveDocument.partId === targetDoc.partId && 
                               moveDocument.revisionId === targetDoc.revisionId;
        
        const sourceStepIdRef = sourceChildNode?.step_id_ref;
        const sourceSubstepIdRef = sourceChildNode?.substep_id_ref || sourceChildNode?.sub_step_tree_node_id ? (sourceChildNode.substep_id_ref || sourceChildNode.sub_step_tree_node_id) : null;
        const targetStepIdRef = targetSubstep ? targetStep.step_id_ref : targetStep.step_id_ref;
        const targetSubstepIdRef = targetSubstep ? targetSubstep.substep_id_ref : null;
        const isSameContainer = sourceStepIdRef === targetStepIdRef && 
                                sourceSubstepIdRef === targetSubstepIdRef;

        if (moveCreateCopy) {
          // Copy: just insert, don't remove
          if (position === 'above') {
            targetContainer.children.splice(targetIndex, 0, clonedChildNode);
          } else {
            targetContainer.children.splice(targetIndex + 1, 0, clonedChildNode);
          }
        } else {
          // Move: remove from source, insert at target
          if (isSameDocument && isSameContainer) {
            // Same-container move: remove from source position first
            const sourceIndex = targetContainer.children.findIndex(
              child => {
                // For canvas nodes, check both detail.canvas_tree_node_id and tree_node_id
                const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
                const childCanvasId = childNodeType === 'canvas' || childNodeType === 'canva' 
                  ? (child.detail?.canvas_tree_node_id || child.tree_node_id)
                  : null;
                const sourceCanvasId = childNodeType === 'canvas' || childNodeType === 'canva'
                  ? (sourceChildNode.detail?.canvas_tree_node_id || sourceChildNode.tree_node_id)
                  : null;
                
                // Check if this child matches the source by any ID field
                return (childId === sourceChildNodeId || child.tree_node_id === sourceChildNodeId) ||
                       (childCanvasId && sourceCanvasId && childCanvasId === sourceCanvasId);
              }
            );
            
            if (sourceIndex !== -1) {
              targetContainer.children.splice(sourceIndex, 1);
              
              // Adjust target index if needed
              let adjustedTargetIndex = targetIndex;
              if (sourceIndex < targetIndex) {
                adjustedTargetIndex = targetIndex - 1;
              }
              
              // Insert at adjusted position
              if (position === 'above') {
                targetContainer.children.splice(adjustedTargetIndex, 0, clonedChildNode);
              } else {
                targetContainer.children.splice(adjustedTargetIndex + 1, 0, clonedChildNode);
              }
            } else {
              // Source not found, just insert
              if (position === 'above') {
                targetContainer.children.splice(targetIndex, 0, clonedChildNode);
              } else {
                targetContainer.children.splice(targetIndex + 1, 0, clonedChildNode);
              }
            }
          } else {
            // Cross-container or cross-document move: remove from source, insert at target
            if (sourceSubstepIdRef) {
              // Remove from source substep
              let sourceSubstep = null;
              
              for (const section of newContent) {
                for (const step of section.steps || []) {
                  sourceSubstep = step.substeps?.find(
                    substep => substep.substep_id_ref === sourceSubstepIdRef ||
                              substep.tree_node_id === sourceSubstepIdRef
                  );
                  if (sourceSubstep) break;
                }
                if (sourceSubstep) break;
              }
              
              if (sourceSubstep && sourceSubstep.children) {
                sourceSubstep.children = sourceSubstep.children.filter(
                  child => {
                    // For canvas nodes, check both detail.canvas_tree_node_id and tree_node_id
                    const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
                    const childCanvasId = childNodeType === 'canvas' || childNodeType === 'canva' 
                      ? (child.detail?.canvas_tree_node_id || child.tree_node_id)
                      : null;
                    const sourceCanvasId = childNodeType === 'canvas' || childNodeType === 'canva'
                      ? (sourceChildNode.detail?.canvas_tree_node_id || sourceChildNode.tree_node_id)
                      : null;
                    
                    // Check if this child matches the source by any ID field
                    const matches = (childId === sourceChildNodeId || child.tree_node_id === sourceChildNodeId) ||
                                   (childCanvasId && sourceCanvasId && childCanvasId === sourceCanvasId);
                    return !matches;
                  }
                );
              }
            } else if (sourceStepIdRef) {
              // Remove from source step
              let sourceStep = null;
              
              for (const section of newContent) {
                sourceStep = section.steps?.find(step => step.step_id_ref === sourceStepIdRef);
                if (sourceStep) break;
              }
              
              if (sourceStep && sourceStep.children) {
                sourceStep.children = sourceStep.children.filter(
                  child => {
                    // For canvas nodes, check both detail.canvas_tree_node_id and tree_node_id
                    const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
                    const childCanvasId = childNodeType === 'canvas' || childNodeType === 'canva' 
                      ? (child.detail?.canvas_tree_node_id || child.tree_node_id)
                      : null;
                    const sourceCanvasId = childNodeType === 'canvas' || childNodeType === 'canva'
                      ? (sourceChildNode.detail?.canvas_tree_node_id || sourceChildNode.tree_node_id)
                      : null;
                    
                    // Check if this child matches the source by any ID field
                    const matches = (childId === sourceChildNodeId || child.tree_node_id === sourceChildNodeId) ||
                                   (childCanvasId && sourceCanvasId && childCanvasId === sourceCanvasId);
                    return !matches;
                  }
                );
              }
            }
            
            // Insert at target position
            if (position === 'above') {
              targetContainer.children.splice(targetIndex, 0, clonedChildNode);
            } else {
              targetContainer.children.splice(targetIndex + 1, 0, clonedChildNode);
            }
          }
        }
      }
    }
  }

  return newContent;
};

/**
 * Build version content for copy operations
 */
export const buildChildNodeCopyVersionContent = ({
  action_to_show,
  allRevisionNodes,
  newContent,
  moveType,
  revision,
  status,
  type,
  partNumber,
  targetSectionIndex,
  targetStepIndex,
  item_code_type
}) => {
  // Map "above" to "up" to match API expectations
  const mappedMoveType = moveType === 'above' ? 'up' : moveType;
  
  // Format: "Section 3 > Step 9 in TARGETREV-19, Rev A-"
  const stepIndex = targetStepIndex !== undefined ? targetStepIndex + 1 : '';
  
  return {
    action_to_show,
    moveType: mappedMoveType,
    rev: revision,
    status,
    type,
    item_code_type: item_code_type,
    partNumber,
    move_copy_action: `Section ${targetSectionIndex + 1} > Step ${stepIndex} in ${partNumber}, Rev ${revision}`,
  };
};

/**
 * Build version content for move operations
 */
export const buildChildNodeMoveVersionContent = ({
  action_to_show,
  allRevisionNodes,
  newContent,
  moveType,
  revision,
  status,
  item_code_type,
  type,
  partNumber,
  partDescription,
  targetSectionIndex,
  targetStepIndex,
}) => {
  // Map "above" to "up" to match API expectations
  const mappedMoveType = moveType === 'above' ? 'up' : moveType;
  
  // Format: "Section 3 > Step 9 in TARGETREV-19, Rev A-"
  const stepIndex = targetStepIndex !== undefined ? targetStepIndex + 1 : '';
  
  return {
    action_to_show,
    moveType: mappedMoveType,
    rev: revision,
    status,
    type,
    item_code_type,
    partNumber,
    partDescription,
    move_copy_action: `Section ${targetSectionIndex + 1} > Step ${stepIndex} in ${partNumber}, Rev ${revision}`,
  };
};

/**
 * Clean up copy payload by removing empty/null values
 */
export const cleanupChildNodeCopyPayload = (payload) => {
  const cleaned = { ...payload };
  
  // Remove undefined values (but keep null and empty strings where needed)
  Object.keys(cleaned).forEach(key => {
    if (cleaned[key] === undefined) {
      delete cleaned[key];
    }
  });

  return cleaned;
};

/**
 * Clean up move payload by removing empty/null values
 */
export const cleanupChildNodeMovePayload = (payload) => {
  const cleaned = { ...payload };
  
  // Remove undefined values (but keep null and empty strings where needed)
  Object.keys(cleaned).forEach(key => {
    if (cleaned[key] === undefined) {
      delete cleaned[key];
    }
  });

  return cleaned;
};

/**
 * Build copy payload for child node (attachment)
 */
export const buildChildNodeCopyPayload = async ({
  sourceChildNode,
  moveDocument,
  targetDoc,
  position,
  movePosition,
  toOrder,
  toProcedureTreeNodeRef,
  toSectionIdRef,
  toStepIdRef,
  toSubstepIdRef,
  toChildNodeRef,
  direction,
  direction_move_into,
  moveComment,
  targetAllNodesOriginal,
  newContent,
  childNodeType,
  dispatch
}) => {
  // Get the node ID key based on child node type
  const getNodeIdKey = (type) => {
    const keyMap = {
      'attachment': 'attachment_tree_node_id',
      'video': 'video_tree_node_id',
      'rich-text': 'rich_text_node_id',
      'canvas': 'canvas_tree_node_id',
      'canva': 'canvas_tree_node_id',
      'form': 'form_tree_node_id',
    };
    return keyMap[type] || 'tree_node_id';
  };

  const nodeIdKey = getNodeIdKey(childNodeType);
  const sourceChildNodeId = sourceChildNode.detail?.[nodeIdKey] || sourceChildNode.tree_node_id;
  
  // Find parent container (step or substep) that contains this child node
  let parentContainerId = null;
  for (const section of targetAllNodesOriginal) {
    for (const step of section.steps || []) {
      // Check if child is in step's children
      const stepChild = step.children?.find(child => {
        const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
        return childId === sourceChildNodeId || child.tree_node_id === sourceChildNodeId;
      });
      if (stepChild) {
        parentContainerId = step.tree_node_id;
        break;
      }
      
      // Check if child is in substep's children
      for (const substep of step.substeps || []) {
        const substepChild = substep.children?.find(child => {
          const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
          return childId === sourceChildNodeId || child.tree_node_id === sourceChildNodeId;
        });
        if (substepChild) {
          parentContainerId = substep.tree_node_id;
          break;
        }
      }
      if (parentContainerId) break;
    }
    if (parentContainerId) break;
  }

  // Build action_to_show using generateTitlesFromStructure
  let action_to_show = '';
  
  // Generate titles for the target document structure
  const nodeTitles = generateTitlesFromStructure(targetAllNodesOriginal);
  
  // Find source section, step, substep, and child node titles
  let sourceSectionTitle = '';
  let sourceStepTitle = '';
  let sourceSubstepTitle = '';
  let sourceChildNodeTitle = '';
  
  for (const section of targetAllNodesOriginal) {
    if (sourceChildNode.section_id_ref && section.section_id_ref === sourceChildNode.section_id_ref) {
      sourceSectionTitle = nodeTitles[section.tree_node_id] || '';
      
      // Find source step
      const sourceStep = section.steps?.find(step => step.step_id_ref === sourceChildNode.step_id_ref);
      if (sourceStep) {
        sourceStepTitle = nodeTitles[sourceStep.tree_node_id] || '';
        
        // Find source substep if exists
        if (sourceChildNode.substep_id_ref || sourceChildNode.sub_step_tree_node_id) {
          const substepIdRef = sourceChildNode.substep_id_ref || sourceChildNode.sub_step_tree_node_id;
          const sourceSubstep = sourceStep.substeps?.find(sub => 
            sub.substep_id_ref === substepIdRef || sub.tree_node_id === substepIdRef
          );
          if (sourceSubstep) {
            sourceSubstepTitle = nodeTitles[sourceSubstep.tree_node_id] || '';
          }
        }
      }
      break;
    }
  }
  
  // Get source child node title
  sourceChildNodeTitle = nodeTitles[sourceChildNode.tree_node_id] || '';
  // If not found in titles, fallback to using getChildNodeDisplayNumber
  if (!sourceChildNodeTitle) {
    const sourceNodeNumber = getChildNodeDisplayNumber(sourceChildNode, childNodeType, targetAllNodesOriginal);
    const childTypeLabel = formatChildTypeLabel(childNodeType);
    sourceChildNodeTitle = `${childTypeLabel} ${sourceNodeNumber}`;
  }
  
  // Find target section, step, substep, and child node titles
  let targetSectionTitle = '';
  let targetStepTitle = '';
  let targetSubstepTitle = '';
  let targetChildNodeTitle = '';
  
  if (position === 'into') {
    // For "into" operations, find target step or substep
    if (movePosition.targetType === 'step') {
      for (const section of targetAllNodesOriginal) {
        const targetStep = section.steps?.find(
          step => step.tree_node_id === movePosition.targetId || 
                  step.step_id_ref === movePosition.targetId
        );
        if (targetStep) {
          targetSectionTitle = nodeTitles[section.tree_node_id] || '';
          targetStepTitle = nodeTitles[targetStep.tree_node_id] || '';
          break;
        }
      }
    } else if (movePosition.targetType === 'substep') {
      for (const section of targetAllNodesOriginal) {
        for (const step of section.steps || []) {
          const targetSubstep = step.substeps?.find(
            substep => substep.tree_node_id === movePosition.targetId || 
                       substep.substep_id_ref === movePosition.targetId
          );
          if (targetSubstep) {
            targetSectionTitle = nodeTitles[section.tree_node_id] || '';
            targetStepTitle = nodeTitles[step.tree_node_id] || '';
            targetSubstepTitle = nodeTitles[targetSubstep.tree_node_id] || '';
            break;
          }
        }
        if (targetSubstepTitle) break;
      }
    }
  } else {
    // For "above" or "below", find target child node and its parent containers
    const targetChildDetails = getTargetChildDetails(targetAllNodesOriginal, movePosition);
    if (targetChildDetails) {
      targetSectionTitle = nodeTitles[targetChildDetails.section.tree_node_id] || '';
      targetStepTitle = nodeTitles[targetChildDetails.step.tree_node_id] || '';
      if (targetChildDetails.substep) {
        targetSubstepTitle = nodeTitles[targetChildDetails.substep.tree_node_id] || '';
      }
      targetChildNodeTitle = nodeTitles[targetChildDetails.child.tree_node_id] || '';
      // If not found in titles, fallback to using getChildNodeDisplayNumber
      if (!targetChildNodeTitle) {
        const targetChildTypeLabel = formatChildTypeLabel(targetChildDetails.child.type);
        const targetChildNumber = getChildNodeDisplayNumber(targetChildDetails.child, targetChildDetails.child.type, targetAllNodesOriginal);
        targetChildNodeTitle = `${targetChildTypeLabel} ${targetChildNumber}`;
      }
    }
  }
  
  // Build source path
  const sourcePath = sourceSubstepTitle 
    ? `${sourceSectionTitle}->${sourceStepTitle}->${sourceSubstepTitle}`
    : `${sourceSectionTitle}->${sourceStepTitle}`;
  
  // Build target path
  let targetPath = targetSubstepTitle
    ? `${targetSectionTitle}->${targetStepTitle}->${targetSubstepTitle}`
    : `${targetSectionTitle}->${targetStepTitle}`;
  
  // Build action_to_show
  if (position === 'into') {
    action_to_show = `Copied ${sourceChildNodeTitle} from ${sourcePath} into ${targetPath}  in ${targetDoc.partNumber}, Rev ${targetDoc.revision}`;
  } else {
    const positionText = position === 'above' ? 'above' : 'below';
    action_to_show = `Copied ${sourceChildNodeTitle} from ${sourcePath} into ${targetPath} ${positionText} ${targetChildNodeTitle}  in ${targetDoc.partNumber}, Rev ${targetDoc.revision}`;
  }

  if (action_to_show.startsWith('Copied ')) {
    action_to_show = action_to_show.replace(/^Copied\s+/, 'User Copied ');
  } else if (!action_to_show.startsWith('User Copied')) {
    action_to_show = `User Copied ${action_to_show}`;
  }

  // Build version content
  const versionContent = buildChildNodeCopyVersionContent({
    action_to_show,
    allRevisionNodes: targetAllNodesOriginal,
    newContent,
    moveType: position,
    revision: targetDoc.revision,
    status: targetDoc.status || 'Not Submitted',
    item_code_type: targetDoc.type || 'Document',
    partNumber: targetDoc.partNumber,
    targetSectionIndex: movePosition.targetSectionIndex !== undefined ? movePosition.targetSectionIndex : 0,
    targetStepIndex: movePosition.targetIndex !== undefined ? movePosition.targetIndex : undefined,
  });

  // Build audit trail data
  // Note: For COPY operations, action should match the child node type
  const actionMap = {
    'attachment': 'ATTACHMENT_COPIED',
    'video': 'VIDEO_COPIED',
    'rich-text': 'RICHTEXT_COPIED',
    'canvas': 'CANVAS_COPIED',
    'canva': 'CANVAS_COPIED',
    'form': 'FORM_COPIED',
  };
  const action = actionMap[childNodeType] || 'CHILD_NODE_COPIED';
  
  // For COPY operations, parent_version should be the source child node's version_id_ref
  const parentVersion = sourceChildNode.version_id_ref || sourceChildNode.detail?.version_id_ref || null;
  
  const previous_content_snapshot = await uploadJsonToS3(targetAllNodesOriginal, targetDoc.partNumber, targetDoc.revision + `${action}_previous`, true);
  const current_snapshot = await uploadJsonToS3(
    prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDoc.partNumber,
      procedure_rev: targetDoc.revision,
      procedure_status: targetDoc.status || 'Not Submitted',
      item_code_type: targetDoc.type,
      partDescription: targetDoc.description || "",
      part_ref: targetDoc.partId,
      procedure_rev_ref: targetDoc.revisionId
    }), 
    targetDoc.partNumber, targetDoc.revision);

  const current_updated_snapshot = await uploadJsonToS3(
    prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDoc.partNumber,
      procedure_rev: targetDoc.revision,
      procedure_status: targetDoc.status || 'Not Submitted',
      item_code_type: targetDoc.type,
      partDescription: targetDoc.description || "",
      part_ref: targetDoc.partId,
      procedure_rev_ref: targetDoc.revisionId
    }), 
    targetDoc.partNumber, targetDoc.revision+"_current_updated_snapshot",true);
  
  const revAndTraningLogs = await getRevAndTraining(dispatch,targetDoc.partId);
  const new_content_snapshot = await uploadJsonToS3({
    data:newContent,
    action_to_show: versionContent.action_to_show,
    item_code_type: targetDoc.type,
    partNumber: targetDoc.partNumber,
    status: targetDoc.status || 'Not Submitted',
    revision: targetDoc.revision,
    operation: "single_audit_trail",
    tree_node_id: sourceChildNode.tree_node_id,
    ...revAndTraningLogs
  }, targetDoc.partNumber, targetDoc.revision + `${action}_new`, true);
  
  const audit_trail_data = await getAuditTrailData(
    {
      action: action,
      comment: moveComment,
      parent_version: parentVersion, // Source child node's version_id_ref for copy operations
      version_content: versionContent,
      previous_content_snapshot,
      new_content_snapshot
    },
    false
  );
  
  // Override hash to empty string for copy operations
  audit_trail_data.hash = '';
  audit_trail_data.version_content = {
    action_to_show: versionContent.action_to_show,
  }

  // Normalize node type for payload
  const normalizeType = (type) => {
    if (type === 'canva') return 'canvas';
    if (type === 'rich-text') return 'rich-text';
    return type;
  };

  const normalizedType = normalizeType(childNodeType);
  
  // Build base payload
  // Note: from_procedure_tree_node_ref should be the source child node's tree_node_id, not parent container

  const payload = {
    from_procedure_tree_node_ref: sourceChildNode.tree_node_id, // Source child node's tree_node_id
    from_order: parseInt(sourceChildNode.sibling_order || sourceChildNode.procedure_tree?.sibling_order || '1', 10),
    from_type: normalizedType,
    from_part_id_ref: moveDocument.partId,
    from_procedure_rev_ref: moveDocument.revisionId,
    from_section_id_ref: sourceChildNode.section_id_ref,
    from_step_id_ref: sourceChildNode.step_id_ref || null,
    from_substep_id_ref: sourceChildNode.substep_id_ref || sourceChildNode.sub_step_tree_node_id || null,
    to_order: toOrder,
    to_type: normalizedType, // Same as source type
    to_part_id_ref: targetDoc.partId,
    to_procedure_rev_ref: targetDoc.revisionId,
    to_section_id_ref: toSectionIdRef,
    to_step_id_ref: toStepIdRef || null,
    to_substep_id_ref: toSubstepIdRef || null,
    from_child_node_ref: sourceChildNodeId, // Child node's ID (e.g., attachment_tree_node_id)
    direction,
    direction_move_into: direction_move_into !== undefined ? direction_move_into : true,
    audit_trail_data,
    current_snapshot,
    current_updated_snapshot,
    node_indexing: {
      [sourceChildNode.tree_node_id]: {
        title: action_to_show,
        previous_content_snapshot,
        new_content_snapshot
      }
    },
  };
  
  // For "into" operations, omit to_procedure_tree_node_ref and to_child_node_ref
  // For "above"/"below" operations, include both
  if (position !== 'into') {
    payload.to_procedure_tree_node_ref = toProcedureTreeNodeRef; // Target child node's tree_node_id
    payload.to_child_node_ref = toChildNodeRef; // Target child node's ID
  }
  // For "into", these fields are omitted (not null, but not included in payload)

  return cleanupChildNodeCopyPayload(payload);
};

/**
 * Build move payload for child node (attachment)
 */
export const buildChildNodeMovePayload = async ({
  sourceChildNode,
  moveDocument,
  targetDoc,
  position,
  movePosition,
  moveNode,
  toOrder,
  toProcedureTreeNodeRef,
  toSectionIdRef,
  toStepIdRef,
  toSubstepIdRef,
  toChildNodeRef,
  direction,
  direction_move_into,
  moveComment,
  targetAllNodesOriginal,
  newContent,
  childNodeType,
  sourceAllNodes,
  dispatch
}) => {
  // Get the node ID key based on child node type
  const getNodeIdKey = (type) => {
    const keyMap = {
      'attachment': 'attachment_tree_node_id',
      'video': 'video_tree_node_id',
      'rich-text': 'rich_text_node_id',
      'canvas': 'canvas_tree_node_id',
      'canva': 'canvas_tree_node_id',
      'form': 'form_tree_node_id',
    };
    return keyMap[type] || 'tree_node_id';
  };

  const nodeIdKey = getNodeIdKey(childNodeType);
  const sourceChildNodeId = sourceChildNode.detail?.[nodeIdKey] || sourceChildNode.tree_node_id;
  
  // Find parent container (step or substep) that contains this child node
  let parentContainerId = null;
  for (const section of targetAllNodesOriginal) {
    for (const step of section.steps || []) {
      // Check if child is in step's children
      const stepChild = step.children?.find(child => {
        const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
        return childId === sourceChildNodeId || child.tree_node_id === sourceChildNodeId;
      });
      if (stepChild) {
        parentContainerId = step.tree_node_id;
        break;
      }
      
      // Check if child is in substep's children
      for (const substep of step.substeps || []) {
        const substepChild = substep.children?.find(child => {
          const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
          return childId === sourceChildNodeId || child.tree_node_id === sourceChildNodeId;
        });
        if (substepChild) {
          parentContainerId = substep.tree_node_id;
          break;
        }
      }
      if (parentContainerId) break;
    }
    if (parentContainerId) break;
  }

  // Build action_to_show
  let action_to_show = '';
  const sourceNodeNumber = getChildNodeDisplayNumber(sourceChildNode, childNodeType, targetAllNodesOriginal);
  const childTypeLabel = formatChildTypeLabel(childNodeType);
  const effectiveSourceNodes = Array.isArray(sourceAllNodes) && sourceAllNodes.length
    ? sourceAllNodes
    : targetAllNodesOriginal;
  
  // Find source section and step indices
  let sourceSectionIndex = 0;
  let sourceStepIndex = 0;
  let sourceStepNumber = 0;
  let sourceSubstepIdentifier = '';
  let sourceSubstep = null;
  
  for (let i = 0; i < targetAllNodesOriginal.length; i++) {
    const section = targetAllNodesOriginal[i];
    if (sourceChildNode.section_id_ref && section.section_id_ref === sourceChildNode.section_id_ref) {
      sourceSectionIndex = i;
      const stepIndex = section.steps?.findIndex(step => step.step_id_ref === sourceChildNode.step_id_ref);
      if (stepIndex !== -1) {
        sourceStepIndex = stepIndex;
        const step = section.steps[stepIndex];
        sourceStepNumber = calculateGlobalStepNumber(targetAllNodesOriginal, step);
        
        // Check if source child node is in a substep
        if (sourceChildNode.substep_id_ref || sourceChildNode.sub_step_tree_node_id) {
          const substepIdRef = sourceChildNode.substep_id_ref || sourceChildNode.sub_step_tree_node_id;
          const substep = step.substeps?.find(sub => 
            sub.substep_id_ref === substepIdRef || sub.tree_node_id === substepIdRef
          );
          if (substep) {
            sourceSubstep = substep;
            const substepLetter = getSubstepLetter(targetAllNodesOriginal, substep);
            if (substepLetter) {
              sourceSubstepIdentifier = `->Substep ${sourceStepNumber}${substepLetter}`;
            }
          }
        }
      }
      break;
    }
  }
  
  let targetSectionNumber = movePosition.targetSectionIndex !== undefined
    ? movePosition.targetSectionIndex + 1
    : movePosition.sectionIndex !== undefined
      ? movePosition.sectionIndex + 1
      : '';

  let targetStepNumber = movePosition.globalStepNumber !== undefined
    ? movePosition.globalStepNumber
    : movePosition.targetStepIndex !== undefined
      ? movePosition.targetStepIndex + 1
      : movePosition.stepIndex !== undefined
        ? movePosition.stepIndex + 1
        : movePosition.targetIndex !== undefined && (movePosition.targetType === 'step' || movePosition.targetType === 'substep')
          ? movePosition.targetIndex + 1
          : '';

  let targetSubstepIdentifier = '';
  const fallbackSubstepIndex = (() => {
    if (movePosition.targetSubstepIndex !== undefined) return movePosition.targetSubstepIndex;
    if (movePosition.substepIndex !== undefined) return movePosition.substepIndex;
    if (movePosition.targetType === 'substep' && movePosition.targetIndex !== undefined) return movePosition.targetIndex;
    return undefined;
  })();

  if (fallbackSubstepIndex !== undefined && fallbackSubstepIndex !== null) {
    const indexNumber = parseInt(fallbackSubstepIndex, 10);
    if (!Number.isNaN(indexNumber)) {
      const fallbackLetter = String.fromCharCode(65 + indexNumber);
      targetSubstepIdentifier = `${targetStepNumber || ''}${fallbackLetter}`;
    }
  } else if (movePosition.substepLetter) {
    targetSubstepIdentifier = `${targetStepNumber || ''}${movePosition.substepLetter}`;
  }

  const targetSubstepDetails = getTargetSubstepDetails(targetAllNodesOriginal, movePosition);
  if (targetSubstepDetails) {
    if (targetSubstepDetails.sectionNumber !== null && targetSubstepDetails.sectionNumber !== undefined) {
      targetSectionNumber = targetSubstepDetails.sectionNumber;
    }
    if (targetSubstepDetails.stepNumber !== null && targetSubstepDetails.stepNumber !== undefined) {
      targetStepNumber = targetSubstepDetails.stepNumber;
    }
    targetSubstepIdentifier = targetSubstepDetails.identifier || targetSubstepIdentifier;
  }

  const targetChildDetails = position !== 'into' ? getTargetChildDetails(targetAllNodesOriginal, movePosition) : null;
  if (targetChildDetails) {
    if (targetChildDetails.sectionNumber !== null && targetChildDetails.sectionNumber !== undefined) {
      targetSectionNumber = targetChildDetails.sectionNumber;
    }
    if (targetChildDetails.stepNumber !== null && targetChildDetails.stepNumber !== undefined) {
      targetStepNumber = targetChildDetails.stepNumber;
    }
    if (targetChildDetails.substepIdentifier) {
      targetSubstepIdentifier = targetChildDetails.substepIdentifier;
    } else if (!targetSubstepIdentifier && targetChildDetails.substep && targetChildDetails.stepNumber) {
      const substepLetter = getSubstepLetter(targetAllNodesOriginal, targetChildDetails.substep);
      targetSubstepIdentifier = substepLetter ? `${targetChildDetails.stepNumber}${substepLetter}` : '';
    }
  }

  // Build source path with substep if applicable
  const sourcePath = sourceSubstepIdentifier 
    ? `Section ${sourceSectionIndex + 1}->Step ${sourceStepNumber}${sourceSubstepIdentifier}`
    : `Section ${sourceSectionIndex + 1}->Step ${sourceStepNumber || sourceStepIndex + 1}`;
  
  if (position === 'into') {
    if (movePosition.targetType === 'step') {
      action_to_show = `Moved ${childTypeLabel} ${sourceNodeNumber} from ${sourcePath} into Section ${targetSectionNumber}->Step ${targetStepNumber}  in ${targetDoc.partNumber}, Rev ${targetDoc.revision}`;
    } else if (movePosition.targetType === 'substep') {
      const substepSegment = targetSubstepIdentifier ? `->Substep ${targetSubstepIdentifier}` : '';
      action_to_show = `Moved ${childTypeLabel} ${sourceNodeNumber} from ${sourcePath} into Section ${targetSectionNumber}->Step ${targetStepNumber}${substepSegment}  in ${targetDoc.partNumber}, Rev ${targetDoc.revision}`;
    }
  } else {
    const positionText = position === 'above' ? 'above' : 'below';
    const substepSegment = targetSubstepIdentifier ? `->Substep ${targetSubstepIdentifier}` : '';

    let targetChildTypeLabel = childTypeLabel;
    let targetChildNumber = movePosition.targetChildNodeNumber || (movePosition.targetIndex !== undefined ? movePosition.targetIndex + 1 : '');

    if (targetChildDetails?.child) {
      targetChildTypeLabel = formatChildTypeLabel(targetChildDetails.child.type) || targetChildTypeLabel;
      targetChildNumber = getChildNodeDisplayNumber(targetChildDetails.child, targetChildDetails.child.type, targetAllNodesOriginal);
    }

    action_to_show = `Moved ${childTypeLabel} ${sourceNodeNumber} from ${sourcePath} into Section ${targetSectionNumber}->Step ${targetStepNumber}${substepSegment ? substepSegment + ' ' : ' '}${positionText} ${targetChildTypeLabel} ${targetChildNumber}  in ${targetDoc.partNumber}, Rev ${targetDoc.revision}`;
  }

  if (action_to_show.startsWith('Moved ')) {
    action_to_show = action_to_show.replace(/^Moved\s+/, 'User Moved ');
  } else if (!action_to_show.startsWith('User Moved')) {
    action_to_show = `User Moved ${action_to_show}`;
  }

  // Normalize node type for payload
  const normalizeType = (type) => {
    if (type === 'canva') return 'canvas';
    if (type === 'rich-text') return 'rich-text';
    return type;
  };
  
    const normalizedType = normalizeType(childNodeType);

  // Build version content
  const actionMap = {
    'attachment': 'ATTACHMENT_MOVED',
    'video': 'VIDEO_MOVED',
    'rich-text': 'RICHTEXT_MOVED',
    'canvas': 'CANVAS_MOVED',
    'canva': 'CANVAS_MOVED',
    'form': 'FORM_MOVED',
  };
  const action = actionMap[childNodeType] || 'CHILD_NODE_MOVED';
  const versionContent = buildChildNodeMoveVersionContent({
    action_to_show,
    allRevisionNodes: targetAllNodesOriginal,
    newContent,
    moveType: position,
    revision: targetDoc.revision,
    status: targetDoc.status || 'Not Submitted',
    type: normalizedType,
    item_code_type: targetDoc.type,
    partNumber: targetDoc.partNumber,
    partDescription: targetDoc.description || '',
    targetSectionIndex: movePosition.targetSectionIndex !== undefined ? movePosition.targetSectionIndex : 0,
    targetStepIndex: movePosition.targetIndex !== undefined ? movePosition.targetIndex : undefined,
  });

  // Build audit trail data
  // Note: For MOVE operations, action should match the child node type

  
  // For MOVE operations, parent_version should be the source child node's version_id_ref
  const parentVersion = sourceChildNode.version_id_ref || sourceChildNode.detail?.version_id_ref || null;

  const previous_content_snapshot = await uploadJsonToS3(targetAllNodesOriginal, targetDoc.partNumber, targetDoc.revision + `${action}_previous`, true);
  const current_snapshot = await uploadJsonToS3(
    prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDoc.partNumber,
      procedure_rev: targetDoc.revision,
      procedure_status: targetDoc.status || 'Not Submitted',
      item_code_type: targetDoc.type,
      partDescription: targetDoc.description || "",
      part_ref: targetDoc.partId,
      procedure_rev_ref: targetDoc.revisionId
    }), 
    targetDoc.partNumber, targetDoc.revision);

  const current_updated_snapshot = await uploadJsonToS3(
    prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDoc.partNumber,
      procedure_rev: targetDoc.revision,
      procedure_status: targetDoc.status || 'Not Submitted',
      item_code_type: targetDoc.type,
      partDescription: targetDoc.description || "",
      part_ref: targetDoc.partId,
      procedure_rev_ref: targetDoc.revisionId
    }), 
    targetDoc.partNumber, targetDoc.revision+"_current_updated_snapshot",true);

  const revAndTraningLogs = await getRevAndTraining(dispatch,moveDocument.partId);
  const new_content_snapshot = await uploadJsonToS3({
    data:newContent,
    action_to_show: versionContent.action_to_show,
    item_code_type: targetDoc.type,
    partNumber: targetDoc.partNumber,
    status: targetDoc.status || 'Not Submitted',
    revision: targetDoc.revision,
    operation: "single_audit_trail",
    tree_node_id: sourceChildNode.tree_node_id,
    ...revAndTraningLogs
  }, targetDoc.partNumber, targetDoc.revision + `${action}_new`, true);

  const audit_trail_data = await getAuditTrailData(
    {
      action: action,
      comment: moveComment,
      parent_version: parentVersion, // Source child node's version_id_ref for move operations
      version_content: versionContent,
      previous_content_snapshot,
      new_content_snapshot,
    },
    false
  );
  
  // Override hash to empty string for move operations
  audit_trail_data.hash = '';
  audit_trail_data.version_content = {
    action_to_show: versionContent.action_to_show,
  }

  
  // Build base payload
  // Note: from_procedure_tree_node_ref should be the source child node's tree_node_id, not parent container

  const fromType = normalizedType === 'canvas' ? 'canva' : normalizedType;
  const payload = {
    from_procedure_tree_node_ref: sourceChildNode.tree_node_id, // Source child node's tree_node_id
    from_order: parseInt(sourceChildNode.sibling_order || sourceChildNode.procedure_tree?.sibling_order || '1', 10),
    from_type: fromType ,
    from_part_id_ref: moveDocument.partId,
    from_procedure_rev_ref: moveDocument.revisionId,
    from_section_id_ref: sourceChildNode.section_id_ref,
    from_step_id_ref: sourceChildNode.step_id_ref || null,
    from_substep_id_ref: sourceChildNode.substep_id_ref || sourceChildNode.sub_step_tree_node_id || null,
    to_order: toOrder,
    to_type: fromType, // Same as source type
    to_part_id_ref: targetDoc.partId,
    to_procedure_rev_ref: targetDoc.revisionId,
    to_section_id_ref: toSectionIdRef,
    to_step_id_ref: toStepIdRef || null,
    to_substep_id_ref: toSubstepIdRef || null,
    from_child_node_ref: sourceChildNodeId, // Child node's ID (e.g., attachment_tree_node_id)
    direction_move_into: direction_move_into !== undefined ? direction_move_into : false,
    direction,
    create_copy: false,
    audit_trail_data,
    current_snapshot,
    current_updated_snapshot,
    node_indexing: {
      [sourceChildNode.tree_node_id]:{
        title:action_to_show,
        previous_content_snapshot:previous_content_snapshot,
        new_content_snapshot:new_content_snapshot
      }
    },
  };
  
  // For "into" operations, omit to_procedure_tree_node_ref and to_child_node_ref
  // For "above"/"below" operations, include both
  if (position !== 'into') {
    payload.to_procedure_tree_node_ref = toProcedureTreeNodeRef; // Target child node's tree_node_id
    payload.to_child_node_ref = toChildNodeRef; // Target child node's ID
  }
  // For "into", these fields are omitted (not null, but not included in payload)

  return cleanupChildNodeMovePayload(payload);
};

